<template>
  <v-dialog
    :model-value="modelValue"
    fullscreen
    :scrim="false"
    class="up-convert-dialog"
    @update:model-value="onDialogUpdate"
  >
    <section class="up-convert" :style="backgroundStyle" role="dialog" aria-modal="true">
      <div class="up-convert__shade"></div>
      <div class="up-convert__content">
        <div class="up-convert__asset">
          <div v-if="assetLogo" class="up-convert__title-art-wrap">
            <img class="up-convert__title-art" :src="assetLogo" :alt="assetTitle">
          </div>
          <h2 v-else class="up-convert__asset-title">{{ assetTitle }}</h2>
          <div v-if="metadataItems.length" class="up-convert__metadata">
            <template v-for="(item, index) in metadataItems" :key="item.key">
              <span
                class="up-convert__metadata-item"
                :class="{ 'up-convert__metadata-item--boxed': item.boxed }"
              >
                {{ item.label }}
              </span>
              <span v-if="index < metadataItems.length - 1" class="up-convert__metadata-separator">•</span>
            </template>
          </div>
        </div>

        <div class="up-convert__panel">
          <flix-latino-logo class="up-convert__logo"></flix-latino-logo>
          <h2 class="up-convert__title">{{ dialogCopy.title }}</h2>
          <p class="up-convert__subtitle" v-html="dialogCopy.subtitle"></p>
          <ul class="up-convert__bullets">
            <li v-for="bullet in dialogCopy.bullets" :key="bullet">
              {{ bullet }}
            </li>
          </ul>
          <button
            v-if="shouldUpgradeDirectly"
            class="up-convert__cta"
            type="button"
            @click="upgradePlan"
          >
            {{ dialogCopy.cta }}
          </button>
          <router-link v-else class="up-convert__cta" :to="ctaRoute" @click="close">
            {{ dialogCopy.cta }}
          </router-link>
          <button class="up-convert__dismiss" type="button" @click="close">
            {{ $t("upConvert.common.dismiss") }}
          </button>
          <p v-if="showFootnote" class="up-convert__footnote">
            {{ $t("upConvert.common.footnote") }}
          </p>
        </div>
      </div>
    </section>
  </v-dialog>
</template>

<script>
import swal from "sweetalert";
import { mapState } from "pinia";
import { UserStore } from "@/stores/userStore";
import { AssetStore } from "@/stores/assetStore";
import { GenresStore } from "@/stores/genresStore";
import Util from "@/services/Util";
import FlixLatinoLogo from "@/components/icons/flixLatinoLogo.vue";
import { FlixlatinoApiClient } from "@/services/FlixlatinoApiClient";


const COPY_KEYS = {
  lockedEpisode: "lockedEpisode",
  premiumMovie: "premiumMovie",
  myList: "myList",
};

export default {
  name: "UpConvertDialog",
  components: { FlixLatinoLogo },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    payload: {
      type: Object,
      default: null,
    },
  },
  emits: ["update:modelValue", "close"],
  computed: {
    ...mapState(UserStore, ["getAccount", "isLoggedIn", "getPlan"]),
    ...mapState(AssetStore, ["getAssetData", "getAssetBySlug"]),
    type() {
      return COPY_KEYS[this.payload?.type] || COPY_KEYS.premiumMovie;
    },
    payloadAsset() {
      return this.payload?.asset || {};
    },
    storeAsset() {
      const id = this.payloadAsset?.id;
      if (id) {
        const asset = this.getAssetData(String(id));
        if (asset) return asset;
      }

      const slug = this.payloadAsset?.slug;
      if (slug) {
        return this.getAssetBySlug(String(slug).replaceAll("_", "-"));
      }

      return null;
    },
    asset() {
      return {
        ...(this.payloadAsset || {}),
        ...(this.storeAsset || {}),
      };
    },
    dialogCopy() {
      const baseKey = "upConvert." + this.type;
      return {
        title: this.$t(baseKey + ".title"),
        subtitle: this.$t(baseKey + ".subtitle"),
        cta: this.$t(baseKey + ".cta"),
        bullets: [0, 1, 2].map((index) =>
          this.$t(baseKey + ".bullets[" + index + "]")
        ),
      };
    },
    ctaRoute() {
      return this.isLoggedIn
        ? this.$t("routes.plans_logged")
        : this.$t("routes.register");
    },
    shouldUpgradeDirectly() {
      return (
        this.isLoggedIn &&
        this.getPlan === "basic" &&
        this.type === "lockedEpisode"
      );
    },
    planDescription() {
      const cycle = this.getAccount?.cycle;
      const planPrice =
        cycle === "monthly"
          ? this.$t("plans.premium.price.monthly")
          : this.$t("plans.premium.price.annual");

      return {
        plan: this.$t("plans.premium.title"),
        price: planPrice,
      };
    },
    showFootnote() {
      return this.type !== "myList";
    },
    assetTitle() {
      return this.asset?.title || "FlixLatino";
    },
    assetLogo() {
      return this.asset?.title_logo || "";
    },
    metadataItems() {
      if (!this.asset || !Object.keys(this.asset).length) return [];

      const items = [];
      const addItem = (key, label, boxed = false) => {
        if (label === null || label === undefined || label === "") return;
        items.push({ key, label: String(label), boxed });
      };

      addItem("year", this.asset.year, true);
      addItem("rating", this.asset.tv_rating, true);

      if (this.asset.type === "series") {
        const configuredSeasonCount = Number(this.asset.season_count);
        const seasonCount =
          Number.isFinite(configuredSeasonCount) && configuredSeasonCount > 0
            ? configuredSeasonCount
            : Array.isArray(this.asset.seasons)
            ? this.asset.seasons.length
            : 0;

        if (Number.isFinite(seasonCount) && seasonCount > 0) {
          const isEnglish = this.$i18n?.locale === "en";
          const label = isEnglish
            ? seasonCount + (seasonCount > 1 ? " Seasons" : " Season")
            : seasonCount + (seasonCount > 1 ? " Temporadas" : " Temporada");
          addItem("seasons", label);
        }
      } else {
        addItem("duration", Util.humanReadableLength(this.asset.duration));
      }

      if (this.asset.genres) {
        const genres = this.asset.genres.map((genre) => {
          const fromStore = GenresStore().getGenre(genre);
          return fromStore ?? genre;
        });
        addItem("genres", genres.slice(0, 2).join(", "));
      }

      addItem("quality", "HD", true);

      return items;
    },
    backdropUrl() {
      const candidates = this.isMobileViewport
        ? [this.asset?.poster]
        : [this.asset?.screenshot];

      return candidates.find((candidate) =>
        typeof candidate === "string" && candidate.trim()
      ) || "";
    },
    backgroundStyle() {
      if (!this.backdropUrl) {
        return { backgroundColor: "#050914" };
      }
      return {
        backgroundImage: `url("${this.backdropUrl}")`,
        backgroundSize: this.isMobileViewport ? "contain" : "cover",
        backgroundPosition: this.isMobileViewport ? "top center" : "center center",
        backgroundRepeat: "no-repeat",
      };
    },
    isMobileViewport() {
      return typeof window !== "undefined" && window.innerWidth < 768;
    },
  },
  methods: {
    close() {
      this.$emit("update:modelValue", false);
      this.$emit("close");
    },
    upgradePlan() {
      const planDescription = this.planDescription;
      swal({
        title: this.$t("upgrade_section.upgrade_plan_title"),
        text: this.$t("upgrade_section.upgrade_plan_text", {
          plan: planDescription.plan,
          price: planDescription.price,
        }),
        icon: "warning",
        buttons: [
          this.$t("upgrade_section.cancel"),
          this.$t("upgrade_section.upgrade"),
        ],
        dangerMode: true,
      }).then((willUpgrade) => {
        if (willUpgrade) {
          this.upgrade();
        }
      });
    },
    upgrade() {
      switch (this.getAccount?.gateway) {
        case "apple":
          window.location.href = "https://apps.apple.com/account/subscriptions";
          break;
        case "braintree":
          this.braintreeUpgrade();
          break;
        case "google":
          window.location.href =
            "https://play.google.com/store/account/subscriptions";
          break;
        case "roku":
          window.location.href = "https://my.roku.com/account/subscriptions";
          break;
        case "amazon":
          window.location.href = "https://www.amazon.com/appstoresubscriptions";
          break;
        default:
          console.log("puede ser de una cablera o promo");
      }
    },
    braintreeUpgrade() {
      const apiClient = new FlixlatinoApiClient();
      apiClient.braintreeUpgrade("upgrade").then((response) => {
        if (response.dev_post) {
          apiClient.getAccount().then(() => {
            this.close();
            this.$router.push({ name: "account" });
          });
        }
      });
    },
    onDialogUpdate(value) {
      if (!value) {
        this.close();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.up-convert-dialog {
  --up-convert-menu-height: 71px;
}

.up-convert-dialog :deep(.v-overlay__content) {
  width: 100vw;
  height: 100vh;
  max-width: none;
  max-height: none;
  margin: 0;
}

.up-convert {
  position: relative;
  width: 100vw;
  height: 100vh;
  min-height: 540px;
  overflow: auto;
  background-color: #050914;
  background-repeat: no-repeat;
  background-position: center center;
  background-size: cover;
  color: #ffffff;
}

.up-convert__shade {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(0deg, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.12)),
    linear-gradient(90deg, rgba(0, 0, 0, 0.22) 0%, rgba(0, 0, 0, 0.08) 45%, rgba(0, 0, 0, 0.28) 100%),
    linear-gradient(0deg, rgba(0, 0, 0, 0.74) 0%, rgba(0, 0, 0, 0.04) 42%, rgba(0, 0, 0, 0.28) 100%);
  pointer-events: none;
}

.up-convert__content {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) clamp(360px, 36.5vw, 467px);
  align-items: stretch;
  gap: 0;
  min-height: 100vh;
  padding: 0;
}

.up-convert__asset {
  align-self: end;
  max-width: 560px;
  margin: 0 clamp(2rem, 7vw, 7rem);
  padding-bottom: clamp(1rem, 5vh, 4rem);
}

.up-convert__title-art-wrap {
  max-width: min(420px, 80vw);
}

.up-convert__title-art {
  display: block;
  max-width: 100%;
  max-height: 170px;
  object-fit: contain;
}

.up-convert__asset-title {
  margin: 0;
  color: #ffffff;
  font-size: clamp(2.5rem, 7vw, 6rem);
  font-weight: 800;
  line-height: 0.96;
  text-shadow: 0 4px 22px rgba(0, 0, 0, 0.55);
}

.up-convert__metadata {
  display: flex;
  align-items: center;
  max-width: 100%;
  margin: 1rem 0 0;
  overflow: hidden;
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  gap: 0.7rem;
  text-shadow: 0 3px 18px rgba(0, 0, 0, 0.65);
  white-space: nowrap;
}

.up-convert__metadata-item {
  display: inline-flex;
  align-items: center;
  min-width: 0;
}

.up-convert__metadata-item--boxed {
  min-height: 1.8em;
  padding: 0.1em 0.45em;
  border: 2px solid rgba(255, 255, 255, 0.75);
  border-radius: 5px;
  line-height: 1;
}

.up-convert__metadata-separator {
  color: rgba(255, 255, 255, 0.72);
}

.up-convert__panel {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  max-width: none;
  min-height: 100vh;
  padding: clamp(2.5rem, 4vw, 4.5rem);
  background: rgba(5, 9, 14, 0.68);
  border: 0;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0;
  box-shadow: inset 24px 0 80px rgba(0, 0, 0, 0.2);
  -webkit-backdrop-filter: blur(24px);
  backdrop-filter: blur(24px);
}


.up-convert__logo {
  display: block;
  width: 230px;
  max-width: 70%;
  margin-bottom: 1.75rem;
}

.up-convert__title {
  margin: 0 0 1rem;
  color: #ffffff;
  font-size: clamp(1.8rem, 3vw, 2.35rem);
  font-weight: 900;
  line-height: 1.05;
  text-align: center;
}

.up-convert__subtitle {
  margin: 0 0 1.8rem;
  color: rgba(255, 255, 255, 0.82);
  font-size: 1.18rem;
  font-weight: 800;
  line-height: 1.45;
  text-align: center;
}

.up-convert__subtitle :deep(u) {
  text-decoration: underline !important;
  text-underline-offset: 0.12em;
}

.up-convert__bullets {
  display: grid;
  gap: 1rem;
  margin: 0 0 2.3rem;
  padding: 0;
  list-style: none;
}

.up-convert__bullets li {
  position: relative;
  min-height: 25px;
  padding-left: 43px;
  color: #ffffff;
  font-size: 1rem;
  line-height: 25px;
}

.up-convert__bullets li::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  width: 25px;
  height: 25px;
  background: url("@/assets/img/icons/green-checkmark.svg") no-repeat center center;
  background-size: contain;
}

.up-convert__cta {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 58px;
  padding: 0.8rem 1rem;
  border: 0;
  border-radius: 10px;
  background: #ed0b04;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.2;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  appearance: none;
  font-family: inherit;
}

.up-convert__cta:hover,
.up-convert__cta:focus-visible {
  background: #ff1b14;
  color: #ffffff;
  text-decoration: none;
}

.up-convert__dismiss {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 58px;
  margin: 10px auto 0;
  padding: 0.8rem 1rem;
  border: 2px solid rgba(255, 255, 255, 0.72);
  border-radius: 10px;
  background: transparent;
  color: rgba(255, 255, 255, 0.88);
  font: inherit;
  font-weight: 800;
  line-height: 1.2;
  cursor: pointer;
}

.up-convert__dismiss:hover,
.up-convert__dismiss:focus-visible {
  color: #ffffff;
  text-decoration: none;
  border-color: #ffffff;
}

.up-convert__footnote {
  margin: 1.9rem auto 0;
  max-width: 360px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.92rem;
  line-height: 1.35;
  text-align: center;
}

@media (min-width: 901px) {
  :deep(.up-convert-dialog.v-overlay) {
    --up-convert-menu-height: 86px;
    z-index: 900 !important;
  }

  :deep(.up-convert-dialog.v-overlay .v-overlay__content) {
    top: var(--up-convert-menu-height);
    height: calc(100vh - var(--up-convert-menu-height));
  }

  .up-convert-dialog :deep(.v-overlay) {
    z-index: 900 !important;
  }

  .up-convert-dialog :deep(.v-overlay__content) {
    top: var(--up-convert-menu-height);
    height: calc(100vh - var(--up-convert-menu-height));
  }

  .up-convert {
    height: calc(100vh - var(--up-convert-menu-height));
    min-height: calc(540px - var(--up-convert-menu-height));
  }

  .up-convert__content,
  .up-convert__panel {
    min-height: calc(100vh - var(--up-convert-menu-height));
  }

  .up-convert__panel {
    align-items: center;
    padding-right: 48px;
    padding-left: 48px;
    text-align: center;
  }

  .up-convert__title {
    font-size: 24px;
  }

  .up-convert__subtitle {
    font-size: 18px;
  }

  .up-convert__logo {
    display: none;
  }

  .up-convert__logo,
  .up-convert__title,
  .up-convert__subtitle,
  .up-convert__bullets,
  .up-convert__cta,
  .up-convert__dismiss,
  .up-convert__footnote {
    width: 100%;
    max-width: 100%;
  }

  .up-convert__logo {
    width: 230px;
    margin-right: auto;
    margin-left: auto;
  }

  .up-convert__bullets {
    text-align: left;
  }
}

@media (max-width: 900px) {
  :deep(.up-convert-dialog.v-overlay) {
    z-index: 1200 !important;
  }

  .up-convert {
    height: 100dvh;
    min-height: 100dvh;
    overflow: auto;
    overscroll-behavior: contain;
    padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 36px);
    scroll-padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 36px);
    background-position: top center;
    background-size: contain;
  }

  .up-convert__shade {
    background: linear-gradient(180deg, rgba(5, 9, 20, 0.78) 0%, rgba(5, 9, 20, 0.48) 5.8rem, rgba(5, 9, 20, 0) 11rem);
  }

  .up-convert__content {
    display: flex;
    flex-direction: column;
    min-height: 100dvh;
    gap: 0;
    padding: 0;
  }

  .up-convert__asset {
    display: flex;
    position: relative;
    flex: 0 0 min(56vh, 470px);
    flex-direction: column;
    justify-content: flex-end;
    width: 100%;
    max-width: none;
    margin: 0;
    padding: calc(11vh + 10px) 0.85rem 1.25rem;
  }

  .up-convert__asset::before {
    content: "";
    position: absolute;
    right: 0;
    bottom: -9rem;
    left: 0;
    height: 82%;
    background: linear-gradient(180deg, rgba(5, 9, 20, 0) 0%, rgba(5, 9, 20, 0) 18%, rgba(5, 9, 20, 0.16) 44%, rgba(5, 9, 20, 0.52) 70%, #050914 100%);
    pointer-events: none;
  }

  .up-convert__asset > * {
    position: relative;
    z-index: 1;
  }

  .up-convert__title-art-wrap {
    max-width: min(210px, 58vw);
    margin-top: 0;
    margin-bottom: 0;
  }

  .up-convert__title-art-wrap,
  .up-convert__asset-title,
  .up-convert__metadata {
    transform: none;
  }

  .up-convert__title-art {
    max-height: 76px;
  }

  .up-convert__asset-title {
    max-width: 80vw;
    font-size: 1.65rem;
  }


  .up-convert__metadata {
    margin-top: 0.55rem;
    margin-bottom: 0;
    gap: 0.45rem;
    overflow: hidden;
    font-size: 10.5px;
    line-height: 1.2;
    font-weight: 700;
    white-space: nowrap;
  }

  .up-convert__metadata-item--boxed {
    min-height: 1.7em;
    padding: 0.08em 0.42em;
    border-width: 1px;
    border-radius: 4px;
  }

  .up-convert__panel {
    flex: 1 0 auto;
    display: flex;
    flex-direction: column;
    max-width: none;
    min-height: 0;
    justify-content: flex-start;
    -webkit-backdrop-filter: none;
    backdrop-filter: none;
    margin-top: -9rem;
    padding: calc(5.45rem + 64px) 1.65rem calc(env(safe-area-inset-bottom, 0px) + 54px);
    background: linear-gradient(180deg, rgba(5, 9, 20, 0) 0%, rgba(5, 9, 20, 0.58) 4.8rem, #050914 9rem, #050914 100%);
    border: 0;
    border-radius: 0;
    box-shadow: none;
  }

  .up-convert__logo {
    position: fixed;
    top: calc(env(safe-area-inset-top, 0px) + 1.55rem);
    left: 1.65rem;
    z-index: 5;
    width: auto;
    height: 32px;
    max-width: 54vw;
    margin: 0;
    pointer-events: none;
  }

  .up-convert__title {
    margin-bottom: 0.85rem;
    font-size: 25px;
    line-height: 1.08;
  }

  .up-convert__subtitle {
    margin-bottom: 2.1rem;
    font-size: 1.08rem;
    line-height: 1.35;
  }


  .up-convert__bullets {
    gap: 14px;
    margin-bottom: 2rem;
  }

  .up-convert__bullets li {
    min-height: 25px;
    padding-left: 20px;
    font-size: 14px;
    line-height: 25px;
  }

  .up-convert__bullets li::before {
    top: 6px;
    width: 12px;
    height: 12px;
  }


  .up-convert__cta {
    min-height: 56px;
    margin-top: auto;
    border-radius: 10px;
    font-size: 16px;
    font-weight: 800;
  }

  .up-convert__dismiss {
    margin-top: 10px;
    font-size: 1rem;
  }

  .up-convert__footnote {
    display: none;
  }
}

@media (max-width: 900px) and (max-height: 700px) {
  .up-convert__asset {
    flex-basis: 48vh;
    padding-top: calc(8vh + 10px);
    padding-bottom: 0.85rem;
  }

  .up-convert__title-art-wrap,
  .up-convert__asset-title,
  .up-convert__metadata {
    transform: none;
  }

  .up-convert__panel {
    margin-top: -7rem;
    padding-top: 5.2rem;
  }

  .up-convert__subtitle {
    margin-bottom: 1.15rem;
  }

  .up-convert__bullets {
    gap: 0.85rem;
    margin-bottom: 1.25rem;
  }
}

</style>
